/**
 * Putting an app on the internet.
 *
 * One route: POST /apps/{appId}/web, which takes the document itself. There is no S3
 * presign and no direct-to-bucket PUT any more — one path, so there is no second way for a
 * document to reach the internet and no direct-bucket link to leak.
 *
 * The 300KB cap is checked HERE before the round trip, so someone is told their app is too
 * large by the thing they clicked rather than by a failure after a long upload. It counts
 * BYTES, matching the server's own Buffer.byteLength check exactly — approximating it with
 * string length would disagree with the server on every app containing an em dash.
 */
import { ApiError, request } from './client';

/** What `fetch` actually puts on the wire, and what the server measures. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The server's limit, mirrored so the refusal can happen before the upload. */
export const MAX_DOCUMENT_BYTES = 300_000;

export interface HostedApp {
  webPublished: boolean;
  webVisibility: 'public' | 'password';
  slug: string;
  /** The server confirming it actually STORED the document. */
  htmlHosted?: boolean;
  siteUrl?: string;
}

export interface HostInput {
  appId: string;
  html: string;
  visibility?: 'public' | 'password';
  password?: string;
  schemas?: unknown;
  aiAccess?: string;
}

export async function hostApp(input: HostInput): Promise<HostedApp> {
  if (!input.appId) {
    throw new ApiError('misconfigured', 'This app has no id to publish under.');
  }

  const bytes = byteLength(input.html);
  if (bytes > MAX_DOCUMENT_BYTES) {
    const kb = Math.ceil(bytes / 1000);
    throw new ApiError(
      'refused',
      `This app is too large to publish — it is ${kb}KB and the limit is 300KB. Removing some seeded content usually brings it under.`,
    );
  }

  const hosted = await request<HostedApp>(`/apps/${encodeURIComponent(input.appId)}/web`, {
    method: 'POST',
    body: {
      visibility: input.visibility ?? 'public',
      ...(input.password ? { password: input.password } : {}),
      html: input.html,
      ...(input.schemas ? { schemas: input.schemas } : {}),
      ...(input.aiAccess ? { aiAccess: input.aiAccess } : {}),
    },
  });

  /*
   * Do not trust siteUrl without htmlHosted. The server sends the URL either way, and a URL
   * for a document that was not stored is a link to an empty app — worse than no link,
   * because the owner shares it.
   */
  if (!hosted.htmlHosted) {
    throw new ApiError('server', 'The app was registered but its page was not stored. Try publishing again.');
  }

  return hosted;
}
